export const MANAGER_COACHING_PROGRAM_TYPE = 'MANAGER_COACHING'

export interface SeatBasedLicense {
  eligible: boolean
  program_type: string
  license_id: string
  access_start_date: string
  access_end_date: string
  sessions_total: number
}

export interface SeatBasedLicensesResponse {
  licenses: SeatBasedLicense[]
}

export enum MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS {
  LOADING = 'loading',
  REQUEST_FAILED = 'requestFailed',
  MISSING_LICENSE = 'missingLicense',
  INELIGIBLE = 'ineligible',
  ELIGIBLE = 'eligible',
}

export interface ManagerCoachingSeatEligibility {
  status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS
  license?: SeatBasedLicense
}

/**
 * Maps seat-based-licenses request state (e.g. from an RTK Query hook) to the shared Manager
 * Coaching seat eligibility. The selected license's `eligible` boolean is the only eligibility
 * value; access dates and session counts must not be used to derive it. Consumers get distinct
 * LOADING / REQUEST_FAILED / MISSING_LICENSE / INELIGIBLE / ELIGIBLE states so each can apply
 * its documented fallback. An unstarted or skipped request reports LOADING so seat state reads
 * as unknown.
 */
export const getManagerCoachingSeatEligibility = ({
  isLoading,
  isError,
  data,
}: {
  isLoading: boolean
  isError: boolean
  data?: SeatBasedLicensesResponse
}): ManagerCoachingSeatEligibility => {
  if (isError) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.REQUEST_FAILED }
  }
  if (isLoading || !data) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.LOADING }
  }
  const license = data.licenses?.find(({ program_type }) => program_type === MANAGER_COACHING_PROGRAM_TYPE)
  if (!license) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE }
  }
  return {
    status: license.eligible
      ? MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE
      : MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE,
    license,
  }
}

/**
 * An eligible seat holder keeps the default-ranked card, and an unknown seat state
 * (loading or request failure) must not surface the proactive generic card, so triggered
 * promotion is allowed only for a resolved missing or ineligible license.
 */
export const canPromoteManagerHubBasedOnSeatEligibility = (seatEligibility?: ManagerCoachingSeatEligibility): boolean =>
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE ||
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE
